import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Heart, MapPin, Star } from 'lucide-react';
import { format } from 'date-fns';

interface Favorite {
  id: string;
  created_at: string;
  service_id: number;
  services?: {
    id: number;
    name: string;
    location?: string;
    price?: number;
    unit?: string;
    rating?: number;
    published: boolean;
    image_url?: string;
  };
}

interface FavoriteCardProps {
  favorite: Favorite;
  onClick?: () => void;
  showPublishStatus?: boolean;
  className?: string;
}

export const FavoriteCard: React.FC<FavoriteCardProps> = ({ 
  favorite, 
  onClick,
  showPublishStatus = false,
  className = ""
}) => {
  if (!favorite.services) {
    return null;
  }

  return (
    <Card 
      key={favorite.id}
      className={`${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} ${className}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {favorite.services.image_url && (
            <img
              src={favorite.services.image_url}
              alt={favorite.services.name}
              className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
            />
          )}
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold truncate">{favorite.services.name}</h4>
                {favorite.services.location && (
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <MapPin className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{favorite.services.location}</span>
                  </div>
                )}
              </div>
              
              <div className="text-right flex-shrink-0 ml-4">
                {favorite.services.price && (
                  <div className="font-semibold">
                    KSh {favorite.services.price}
                    {favorite.services.unit && (
                      <span className="text-sm font-normal">/{favorite.services.unit}</span>
                    )}
                  </div>
                )}
                
                {favorite.services.rating && (
                  <div className="flex items-center gap-1 mt-1 justify-end">
                    <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                    <span className="text-sm">{favorite.services.rating}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {showPublishStatus && (
                  <Badge variant={favorite.services.published ? 'default' : 'secondary'}>
                    {favorite.services.published ? 'Published' : 'Unpublished'}
                  </Badge>
                )}
              </div>
              
              <span className="text-xs text-muted-foreground">
                Added {format(new Date(favorite.created_at), 'MMM dd, yyyy')}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};